import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { GraphDetails, MicrosoftTenant, MicrosoftTenantRef } from '../../shared/models/generated/ziroModels';
import { Observable } from 'rxjs';
import { delayWhen } from 'rxjs/operators';
import { MicrosoftTenantsStateContext } from '../../shared/contexts/microsoft-tenants-state.context';

@Injectable()
export class MicrosoftTenantsResource {
  private _http = inject(HttpClient);
  private _tenantsStateContext = inject(MicrosoftTenantsStateContext);

  private _baseUrl = '/services/microsoft/admin/tenants';

  get(id: number): Observable<MicrosoftTenant> {
    return this._get(id);
  }

  post(tenant: MicrosoftTenant): Observable<MicrosoftTenantRef> {
    return this._post(tenant).pipe(delayWhen(() => this._tenantsStateContext.refresh()));
  }

  put(tenant: MicrosoftTenant): Observable<void> {
    return this._put(tenant).pipe(delayWhen(() => this._tenantsStateContext.refresh()));
  }

  delete(id: number): Observable<void> {
    return this._delete(id).pipe(delayWhen(() => this._tenantsStateContext.refresh()));
  }

  validate(tenantDetails: MicrosoftTenant): Observable<GraphDetails> {
    return this._http.post<GraphDetails>(`${this._baseUrl}/validation`, tenantDetails);
  }

  grantedRolesAndPermissions(tenantId: number): Observable<GraphDetails> {
    return this._http.get<GraphDetails>(`${this._baseUrl}/${tenantId}/granted-roles-and-permissions`);
  }

  tokenRefresh(tenantId: number): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/${tenantId}/granted-roles-and-permissions/token-refresh`, null);
  }

  /** Guarded on Global Settings. DO NOT USE! */
  __getAll(): Observable<MicrosoftTenant[]> {
    return this._http.get<MicrosoftTenant[]>(this._baseUrl);
  }

  private _get(id: number): Observable<MicrosoftTenant> {
    return this._http.get<MicrosoftTenant>(`${this._baseUrl}/${id}`);
  }

  private _post(tenant: MicrosoftTenant): Observable<MicrosoftTenantRef> {
    return this._http.post<MicrosoftTenantRef>(this._baseUrl, tenant);
  }

  private _put(tenant: MicrosoftTenant): Observable<void> {
    return this._http.put<void>(`${this._baseUrl}/${tenant.id}`, tenant);
  }

  private _delete(id: number): Observable<void> {
    return this._http.delete<void>(`${this._baseUrl}/${id}`);
  }
}
